/**
 * Shared autocomplete logic for the hero search and the search composer
 * (mobile drawer / desktop sidebar). Pure functions only — no React.
 *
 * Originally lived inline in HeroSearchInput.tsx; lifted here so the
 * filter-panel search input on /search can reuse the same matching,
 * grouping, and counting behavior.
 */

import { byLocale } from "@/lib/locale";
import { SUGGESTIONS, type Suggestion } from "@/lib/data/suggestions";
import type { Locale } from "@/lib/locale";
import type { SuggestionCounts, SpecialtySuggestion } from "@/lib/queries/professionals";
import { isCityKeyword } from "@/lib/city-keywords";
import { synonymAliasesFor } from "@/lib/search-synonyms";

export const MAX_RESULTS = 8;

const CATEGORIES = SUGGESTIONS.filter((s) => s.type === "category");
const LOCATIONS = SUGGESTIONS.filter((s) => s.type === "location");

// Busiest cities, in prominence order, offered for a "<category> in …" query
// that hasn't named a city yet (e.g. "lawyer in "). The full LOCATIONS list is
// all 34 provinces in alphabetical order; using it here would make
// buildCombined's `.slice(0, MAX_RESULTS)` only ever reach the alphabetically
// first provinces (An Giang, Bắc Ninh, …) — none of which have professionals —
// so the downstream zero-count filter would leave nothing ("No professionals
// found"). These majors are where supply actually is, so the combined
// suggestions survive the count filter.
const MAJOR_CITY_SLUGS = ["ho-chi-minh", "ha-noi", "da-nang", "hai-phong", "can-tho", "hue"];
const MAJOR_LOCATIONS: Suggestion[] = MAJOR_CITY_SLUGS
  .map((slug) => LOCATIONS.find((l) => l.id === `loc-${slug}`))
  .filter((s): s is Suggestion => Boolean(s));

/** Connector words used to split "category in location" queries. */
const CONNECTORS = ["in", "ở", "tại", "à", "au"];

/**
 * Lowercase + strip diacritics so matching is accent-insensitive — essential
 * on a Vietnamese site where users type `da nang`, `Đà Nẵng`, or `dà nang`
 * interchangeably (and VN keyboards / ASCII input rarely agree on accents).
 * NFD decomposes accented letters into base + combining mark, which we drop;
 * `đ` is a distinct letter that NFD does NOT decompose, so map it explicitly.
 * Mirrors the ward-index normalisation in `provinces.ts`. Exported so the
 * match-highlighters can normalise too — otherwise an accent-mismatched query
 * matches a suggestion but the bold span doesn't apply.
 */
export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d");
}

function startsWithCI(text: string, query: string): boolean {
  return normalizeText(text).startsWith(normalizeText(query));
}

function includesCI(text: string, query: string): boolean {
  return normalizeText(text).includes(normalizeText(query));
}

/** Convert SpecialtySuggestion[] from the server into Suggestion objects. */
export function buildSpecialtySuggestions(specialties: SpecialtySuggestion[]): Suggestion[] {
  return specialties.map((s) => ({
    id: `spec-${s.specialty.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    label: { en: s.specialty, vi: s.specialty, fr: s.specialty },
    aliases: [],
    // Plain words that mean this specialty ("merge" for "M&A"), from the same
    // synonym rules the search uses, so the dropdown offers what the search finds.
    synonyms: synonymAliasesFor(s.specialty),
    type: "specialty" as const,
    href: `/search?q=${encodeURIComponent(s.specialty)}`,
  }));
}

function matchItems(query: string, locale: Locale, items: Suggestion[]): Suggestion[] {
  if (!query) return [];
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const scored: { s: Suggestion; score: number }[] = [];

  for (const s of items) {
    const localeLabel = byLocale(locale, s.label);
    const enLabel = s.label.en;

    if (startsWithCI(localeLabel, q)) {
      scored.push({ s, score: 1 });
    } else if (locale !== "en" && startsWithCI(enLabel, q)) {
      scored.push({ s, score: 2 });
    } else if (s.aliases.some((a) => startsWithCI(a, q))) {
      scored.push({ s, score: 2 });
    } else if (includesCI(localeLabel, q)) {
      scored.push({ s, score: 3 });
    } else if (s.aliases.some((a) => includesCI(a, q))) {
      scored.push({ s, score: 4 });
    } else if (q.length >= 3 && s.synonyms?.some((a) => startsWithCI(a, q))) {
      // Synonyms only suggest, never outrank: 3+ characters, ranked last.
      scored.push({ s, score: 5 });
    }
  }

  scored.sort((a, b) => a.score - b.score);
  return scored.map((x) => x.s);
}

function splitByConnector(query: string): { catPart: string; locPart: string } | null {
  const q = query.toLowerCase().trim();

  for (const conn of CONNECTORS) {
    const pattern = ` ${conn} `;
    const idx = q.indexOf(pattern);
    if (idx > 0) {
      const catPart = q.slice(0, idx).trim();
      const locPart = q.slice(idx + pattern.length).trim();
      if (catPart) return { catPart, locPart };
    }
  }

  for (const conn of CONNECTORS) {
    const pattern = ` ${conn}`;
    if (q.endsWith(pattern) && q.length > pattern.length) {
      const catPart = q.slice(0, -pattern.length).trim();
      if (catPart) return { catPart, locPart: "" };
    }
  }

  return null;
}

/**
 * Whole-part EXACT match against a suggestion's localised label, English
 * label, or aliases (case-insensitive). Used to gate the *category* side of
 * the connector-less split: exact (not prefix) matching is what stops a 2-char
 * fragment like "ho" from matching the "housing" alias and hijacking
 * "ho chi minh" into "Real Estate in Ho Chi Minh".
 */
function exactMatch(part: string, locale: Locale, items: Suggestion[]): boolean {
  const p = normalizeText(part.trim());
  if (!p) return false;
  return items.some(
    (s) =>
      normalizeText(byLocale(locale, s.label)) === p ||
      normalizeText(s.label.en) === p ||
      s.aliases.some((a) => normalizeText(a) === p),
  );
}

/**
 * PREFIX match against a suggestion's labels/aliases (case-insensitive). Used
 * to gate the *location* side of the connector-less split so a city typed
 * partially ("ha n" → Ha Noi) still matches mid-keystroke. Prefix-only (not
 * substring) so "an" matches "An Giang" but not "Da Nang".
 */
function prefixMatch(part: string, locale: Locale, items: Suggestion[]): boolean {
  const p = normalizeText(part.trim());
  if (!p) return false;
  return items.some(
    (s) =>
      normalizeText(byLocale(locale, s.label)).startsWith(p) ||
      normalizeText(s.label.en).startsWith(p) ||
      s.aliases.some((a) => normalizeText(a).startsWith(p)),
  );
}

/**
 * Connector-less "<category> <location>" / "<location> <category>" detection —
 * e.g. "lawyer hanoi", "hanoi lawyer", or mid-type "lawyer ha n", where the
 * user omitted the "in". Tries every word-boundary split and returns the first
 * where one side is EXACTLY a category and the other a location PREFIX. The
 * asymmetry is deliberate: requiring an exact category keeps multi-word
 * locations ("ho chi minh") and single terms ("hanoi", "real estate") falling
 * through to normal matching, while the location prefix lets the city complete
 * as the user types.
 */
function splitCategoryLocation(
  query: string,
  locale: Locale,
): { catPart: string; locPart: string } | null {
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;

  for (let i = 1; i < tokens.length; i++) {
    const left = tokens.slice(0, i).join(" ");
    const right = tokens.slice(i).join(" ");

    if (exactMatch(left, locale, CATEGORIES) && prefixMatch(right, locale, LOCATIONS)) {
      return { catPart: left, locPart: right };
    }
    if (prefixMatch(left, locale, LOCATIONS) && exactMatch(right, locale, CATEGORIES)) {
      return { catPart: right, locPart: left };
    }
  }

  return null;
}

function buildCombined(categories: Suggestion[], locations: Suggestion[]): Suggestion[] {
  const results: Suggestion[] = [];
  for (const cat of categories) {
    for (const loc of locations) {
      results.push({
        id: `${cat.id}--${loc.id}`,
        label: {
          en: `${cat.label.en} in ${loc.label.en}`,
          vi: `${cat.label.vi} tại ${loc.label.vi}`,
          fr: `${cat.label.fr} à ${loc.label.fr}`,
          // Place first, joined by 的 with no spaces (zh style guide §2).
          "zh-Hans": `${byLocale("zh-Hans", loc.label)}的${byLocale("zh-Hans", cat.label)}`,
          "zh-Hant": `${byLocale("zh-Hant", loc.label)}的${byLocale("zh-Hant", cat.label)}`,
        },
        aliases: [],
        type: "combined",
        href: `${cat.href}&${loc.href.split("?")[1] ?? ""}`,
      });
    }
  }
  return results;
}

/** Filter and rank suggestions against a query in the given locale. */
export function filterSuggestions(
  query: string,
  locale: Locale,
  specialtySuggestions: Suggestion[],
): Suggestion[] {
  if (!query.trim()) return [];

  // "TP" / "TP." / "Thành phố" → the user means a city; offer them all.
  // MAJOR_LOCATIONS is exactly the centrally-managed cities, prominence-ordered.
  if (isCityKeyword(normalizeText(query))) return MAJOR_LOCATIONS;

  const split = splitByConnector(query) ?? splitCategoryLocation(query, locale);
  if (split) {
    const { catPart, locPart } = split;
    const matchedCats = matchItems(catPart, locale, CATEGORIES);
    const matchedLocs = locPart
      ? matchItems(locPart, locale, LOCATIONS)
      : MAJOR_LOCATIONS;

    if (matchedCats.length > 0 && matchedLocs.length > 0) {
      return buildCombined(matchedCats, matchedLocs).slice(0, MAX_RESULTS);
    }
  }

  const staticMatches = matchItems(query, locale, SUGGESTIONS);
  const specMatches = matchItems(query, locale, specialtySuggestions);
  const maxSpec = Math.max(1, MAX_RESULTS - staticMatches.length);
  return [...staticMatches, ...specMatches.slice(0, maxSpec)].slice(0, MAX_RESULTS);
}

/** Levenshtein edit distance (iterative, two-row). Inputs are short taxonomy
 *  terms + a query token, so the O(n·m) cost is trivial. */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * "Did you mean" — the category whose label/alias is a near-miss (1–2 edits)
 * of the query or one of its tokens. Returns the category param value
 * (e.g. "Legal") for a `/search?category=…` link, or null.
 *
 * Diacritic- and case-insensitive (via `normalizeText`). EXACT matches return
 * null: an exact term that yielded zero results means the category itself is
 * empty, so suggesting it would be another dead-end — only typo near-misses
 * are useful here. The candidate set is the fixed taxonomy (10 categories ×
 * a few aliases), so no DB extension / fuzzy index is needed.
 *
 * `counts` (the `get_suggestion_counts` map) drops categories with NO public
 * listings from the candidates, so the next-nearest category that has pros can
 * still win (owner, 2026-09-19: "payrol" offered the empty HR shelf, another
 * dead end). An EMPTY map means the counts failed to load, and then nothing is
 * dropped: unknown is not zero (see `hasCountData`).
 */
export function suggestCategoryForQuery(
  query: string,
  locale: Locale,
  counts?: Record<string, number>,
): string | null {
  const qNorm = normalizeText(query.trim());
  if (qNorm.length < 3) return null;
  const tokens = [qNorm, ...qNorm.split(/\s+/)].filter((t) => t.length >= 3);
  const knowCounts = !!counts && Object.keys(counts).length > 0;

  let bestCat: string | null = null;
  let bestDist = Infinity;

  for (const cat of CATEGORIES) {
    const value = new URLSearchParams(cat.href.split("?")[1] ?? "").get("category");
    if (knowCounts && value && !((counts![value] ?? 0) > 0)) continue;
    const terms = [
      normalizeText(byLocale(locale, cat.label)),
      normalizeText(cat.label.en),
      ...cat.aliases.map(normalizeText),
    ];
    for (const term of terms) {
      if (term.length < 3) continue;
      for (const tok of tokens) {
        const d = levenshtein(tok, term);
        // Tighter tolerance for short words (1 edit) than longer ones (2).
        const tol = Math.max(term.length, tok.length) <= 4 ? 1 : 2;
        if (d > 0 && d <= tol && d < bestDist) {
          bestDist = d;
          bestCat = value;
        }
      }
    }
  }
  return bestCat;
}

/** Group suggestions by type, preserving order within each group. */
export function groupByType(items: Suggestion[]): { type: Suggestion["type"]; items: Suggestion[] }[] {
  const groups: Map<Suggestion["type"], Suggestion[]> = new Map();
  for (const item of items) {
    const arr = groups.get(item.type) ?? [];
    arr.push(item);
    groups.set(item.type, arr);
  }
  const order: Suggestion["type"][] = ["combined", "category", "specialty", "location"];
  return order.filter((t) => groups.has(t)).map((t) => ({ type: t, items: groups.get(t)! }));
}

/** Look up the result count for a suggestion from the counts map. */
export function getCount(
  suggestion: Suggestion,
  counts: SuggestionCounts,
  specialtyCounts: Map<string, number>,
): number {
  if (suggestion.type === "specialty") {
    return specialtyCounts.get(suggestion.label.en.toLowerCase()) ?? 0;
  }

  const params = new URLSearchParams(suggestion.href.split("?")[1] ?? "");
  const cat = params.get("category");
  const loc = params.get("location");

  if (cat && loc) return counts[`${cat}|${loc}`] ?? 0;
  if (cat) return counts[cat] ?? 0;
  if (loc) return counts[loc] ?? 0;
  return 0;
}

/** Pluralised "N result(s)" label per locale. */
/**
 * Do we actually KNOW the result counts, or did the fetch come back empty?
 *
 * ⛔ THE TWO ARE NOT THE SAME FACT, and conflating them shipped a bug (owner
 * caught it 2026-09-08). `getSuggestionCounts()` returns `{}` on ANY failure,
 * so an empty map means "we could not find out", never "there are none". Both
 * dropdowns already skipped the zero-filter in that case — correct, since
 * filtering on unknown counts would hide everything — but they still rendered
 * `resultLabel(0)` on every row. The reader was told **"0 results"** against
 * six cities, which is a claim about the DIRECTORY made from a failed fetch,
 * and it disagreed with whichever surface had loaded its counts successfully.
 *
 * Callers must use this for BOTH decisions: filter only when it is true, and
 * render the count badge only when it is true.
 */
export function hasCountData(
  counts: Record<string, number>,
  specCounts: Map<string, number>,
): boolean {
  return Object.keys(counts).length > 0 || specCounts.size > 0;
}

export function resultLabel(n: number, locale: Locale): string {
  return byLocale(locale, {
    en: (c: number) => (c === 1 ? `${c} result` : `${c} results`),
    vi: (c: number) => `${c} kết quả`,
    fr: (c: number) => (c === 1 ? `${c} résultat` : `${c} résultats`),
    // Chinese does not inflect for number either; 个 is the measure word.
    "zh-Hans": (c: number) => `${c}个结果`,
    "zh-Hant": (c: number) => `${c}個結果`,
  })(n);
}

/** Build the specialty counts map from raw server data. */
export function buildSpecialtyCounts(specialties: SpecialtySuggestion[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of specialties) m.set(s.specialty.toLowerCase(), s.count);
  return m;
}

/**
 * The directory categories a free-text query POINTS AT, for deciding which
 * affiliates a typed search may show (`affiliate-links-spec.md` D5). A
 * category matches when its label (viewer's locale or English) or one of its
 * aliases appears in the query as whole words: "divorce lawyer" and "luật sư
 * hà nội" give Legal, "car insurance" gives Insurance, "weather" gives none.
 * Case-insensitive. Returns canonical category values, in taxonomy order, each
 * once; [] when the query names no category.
 *
 * ACCENTS, PER WORD. A query word typed WITH Vietnamese diacritics must match
 * the term's word exactly ("thuê", rent, is not "thuế", tax: stripping both
 * made "thuê xe", car rental, read as Finance). A word typed WITHOUT
 * diacritics matches the stripped form, because most Vietnamese readers type
 * "luat su" and mean "luật sư"; that one can still be ambiguous, which is
 * accepted.
 *
 * NO TYPO FALLBACK (review 2026-09-15). A near-miss rule turned ordinary words
 * into categories ("villa" into Visa, "local" into Legal, "movie" into
 * Relocation), and for affiliates the safe failure is showing none. English
 * plurals are tolerated ("lawyers", "companies").
 */
export function categoriesForQuery(query: string, locale: Locale): string[] {
  const words = (s: string) => s.toLowerCase().normalize("NFC").replace(/[^\p{L}\p{N}]+/gu, " ").trim().split(" ").filter(Boolean);
  const isAscii = (w: string) => !/[^\x00-\x7F]/.test(w);
  const q = words(query);
  if (q.length === 0) return [];

  const wordMatches = (queryWord: string, termWord: string): boolean => {
    const sameWord = (a: string) => (isAscii(queryWord) ? normalizeText(a) === queryWord : a === queryWord);
    if (sameWord(termWord)) return true;
    // English plurals on the QUERY side: lawyers -> lawyer, companies -> company.
    if (!isAscii(queryWord) || !isAscii(termWord)) return false;
    return queryWord === `${termWord}s` || queryWord === `${termWord}es`
      || (termWord.endsWith("y") && queryWord === `${termWord.slice(0, -1)}ies`);
  };
  const phraseAt = (term: string[], at: number) => term.every((t, i) => q[at + i] !== undefined && wordMatches(q[at + i], t));

  const found: string[] = [];
  for (const cat of CATEGORIES) {
    const terms = [byLocale(locale, cat.label), cat.label.en, ...cat.aliases]
      .map(words)
      .filter((t) => t.join(" ").length >= 3);
    const hit = terms.some((term) => q.some((_, at) => phraseAt(term, at)));
    if (!hit) continue;
    const value = new URLSearchParams(cat.href.split("?")[1] ?? "").get("category");
    if (value && !found.includes(value)) found.push(value);
  }
  return found;
}
